import { Accept, useDropzone } from 'react-dropzone';
import { Box, SxProps, Theme } from '@mui/system';
import { Button, useTheme } from '@mui/material';
import { FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  dropzone,
  dropzoneContainer,
  fileContainer,
  fileImg,
  filesContainer,
} from './styles';

import DeleteIcon from '@mui/icons-material/Delete';
import FileLoaderComponent from './upload-file-loader';
import ImageBoxFuture from '../image-box-future';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Typography from '@mui/material/Typography';
import cloneDeep from 'lodash/cloneDeep';

import { isArray } from '@muvi/react-common';
import { pxToRem } from '@/theme/typography';

const fileIcon = '/icons/contact-us/fileIcon.svg';
const uploadFileIcon = '/icons/contact-us/uploadFileIcon.svg';
const deleteIcon = '/icons/contact-us/deleteIcon.svg';
export interface fileTypes extends File {
  preview: string;
  type: string;
}
export interface FileProps {
  accept: Accept | undefined;
  title?: string;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  multiple?: boolean;
  noDrag?: boolean;
  imgType?: boolean;
  isLoading?: boolean;
  requiredUploadImage?: boolean;
  filesContainerStyle?: SxProps<Theme>;
  dropzoneContainerStyle?: SxProps<Theme>;
  dropzoneStyle?: SxProps<Theme>;
  fileContainerStyle?: SxProps<Theme>;
  fileImgStyle?: SxProps<Theme>;
  fileNameStyle?: SxProps<Theme>;
  deleteIconStyle?: SxProps<Theme>;
  loaderStyle?: SxProps<Theme>;
  handleFiles: (files: fileTypes[]) => void;
  dropzoneText?: string;
  demoLink?: string;
  demoLinkTitle?: string;
  hint?: string;
  fileImg?: string;
  initValues?: string[] | File | string;
  hasAccess?: () => boolean | string;
  errorFile?: boolean;
}
export interface initFilesProps {
  preview: string;
}

export const FileUploadComponent: FC<FileProps> = props => {
  const theme = useTheme();
  const intl = useIntl();

  const [files, setFiles] = useState<fileTypes[]>([]);
  const [initFiles, setInitFiles] = useState<initFilesProps[] | []>([]);
  const [errorFileCondition, setErrorFileCondition] = useState<boolean>(
    props.errorFile || false
  );
  const [maxFileCondition, setMaxFileCondition] = useState<boolean>(false);
  const submittedFiles = useRef<fileTypes[]>([]);
  const onDropFile = useCallback((acceptedFiles: (fileTypes | File)[]) => {
    (submittedFiles.current as fileTypes[] | File[]) = acceptedFiles;
    setErrorFileCondition(false);
    setMaxFileCondition(false);
    if (props.imgType) {
      submittedFiles.current = acceptedFiles.map(file =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );
    }
    setFiles(prev => {
      if (props.multiple === undefined) {
        if (props.maxFiles) {
          if ([...prev, ...submittedFiles.current].length > props.maxFiles) {
            setErrorFileCondition(true);
            return prev;
          }
        }
        if ([...prev, ...submittedFiles.current].length > 1) {
          setErrorFileCondition(true);
          return prev;
        }
      }
      props.handleFiles([...prev, ...submittedFiles.current]);
      return [...prev, ...submittedFiles.current];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFile = useCallback(
    (e: MouseEvent, fileIndex: number, initFile: boolean) => {
      if (initFile) {
        e.preventDefault();
        e.stopPropagation();
        const newInitFiles = cloneDeep(initFiles);
        newInitFiles.splice(fileIndex, 1);
        setInitFiles(newInitFiles);
        setFiles(newInitFiles as fileTypes[]);
        props.handleFiles(newInitFiles as fileTypes[]);
      } else {
        setErrorFileCondition(false);
        setMaxFileCondition(false);
        e.preventDefault();
        e.stopPropagation();
        const newFiles = cloneDeep(files);
        newFiles.splice(fileIndex, 1);
        setFiles(newFiles);
        setInitFiles(newFiles);
        props.handleFiles(newFiles as fileTypes[]);
      }
    },
    [files, initFiles, props]
  );

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: props.accept,
    maxFiles: props.maxFiles,
    maxSize: props.maxSize,
    minSize: props.minSize,
    multiple: props.multiple || false,
    noDrag: props.noDrag || false,
    onDrop: onDropFile,
    onDropRejected: useCallback(() => {
      setErrorFileCondition(true);
    }, []),
  });

  useEffect(() => {
    if (props.initValues && isArray(props.initValues) && props.initValues.length > 0) {
      const submittedValues = props.initValues.map(initVal => ({
        preview: initVal,
      }));
      setInitFiles(submittedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.initValues === undefined) {
      setInitFiles([]);
      setFiles([]);
    }
  }, [props.initValues]);

  return (
    <Box>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        {props.title && (
          <Typography variant='subtitle1' mb={1}>
            <FormattedMessage id={props.title} />
          </Typography>
        )}
      </Box>

      <Box
        sx={
          props.dropzoneContainerStyle
            ? { ...props.dropzoneContainerStyle, position: 'relative' }
            : {
                ...dropzoneContainer,
                borderColor: `${
                  props.requiredUploadImage
                    ? theme.palette.error.light
                    : theme.palette.grey[30]
                }`,
              }
        }
      >
        <Box {...getRootProps({})} sx={dropzone}>
          <input {...getInputProps()} />
          {files.length === 0 && initFiles.length === 0 && (
            <Box
              component='div'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                height: { tablet: pxToRem(193), mobile: '193px' },
              }}
            >
              <ImageBoxFuture
                img={uploadFileIcon}
                title={intl.formatMessage({
                  id: 'global.upload_file_icon',
                })}
                objectFit='contain'
                sx={{
                  p: { tablet: pxToRem(8), mobile: '8px' },

                  height: { tablet: pxToRem(45), mobile: '45px' },
                  width: { tablet: pxToRem(34), mobile: '34px' },
                }}
              />

              <Typography
                component='p'
                sx={{
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: { tablet: pxToRem(16), mobile: '16px' },
                  lineHeight: '150%',
                  color: theme.palette.grey[80],
                }}
              >
                <FormattedMessage
                  id={props.dropzoneText ? props.dropzoneText : 'global.drag_drop_file'}
                />
              </Typography>
              <Typography
                component='p'
                sx={{
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: { tablet: pxToRem(16), mobile: '16px' },

                  lineHeight: '150%',
                  color: theme.palette.secondary.main,
                  mb: { tablet: pxToRem(14), mobile: '14px' },
                }}
              >
                <FormattedMessage
                  id={props.dropzoneText ? props.dropzoneText : 'global.or'}
                />
              </Typography>
              <Button
                id={'avatar-upload'}
                variant='secondary'
                startIcon={
                  <RemoveRedEyeIcon
                    sx={{
                      fontSize: { tablet: pxToRem(16), mobile: '16px' },
                      mr: { tablet: pxToRem(8), mobile: '8px' },
                      width: { tablet: pxToRem(16), mobile: '16px' },
                      height: { tablet: pxToRem(16), mobile: '16px' },
                    }}
                  />
                }
                sx={{
                  width: { tablet: pxToRem(143), mobile: '143px' },
                  height: { tablet: pxToRem(40), mobile: '40px' },
                  p: 0,
                  borderRadius: '12px !important',
                  fontWeight: '700',
                  fontSize: { tablet: pxToRem(16), mobile: '16px' },
                  lineHeight: ' 150%',
                  display: 'flex',
                  '.MuiButton-startIcon': {
                    mr: 0,
                  },
                }}
              >
                <FormattedMessage
                  id={props.dropzoneText ? props.dropzoneText : 'global.browse_file'}
                />
              </Button>
            </Box>
          )}
          {files && files.length > 0
            ? files.map((file: fileTypes, i: number) => (
                <Box
                  key={file.name}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '100%',
                  }}
                >
                  <Box sx={filesContainer} key={file.name}>
                    <Box>
                      {!props.isLoading && (
                        <Button
                          sx={{
                            backgroundColor: '#FDE2EA',
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            borderRadius: '50%',
                            width: '24px',
                            minWidth: 'auto',
                            height: '24px',
                            p: '4px',
                          }}
                          onClick={(e: MouseEvent) => removeFile(e, i, true)}
                        >
                          <ImageBoxFuture
                            img={deleteIcon}
                            title={intl.formatMessage({
                              id: 'global.upload_file_icon',
                            })}
                            objectFit='contain'
                            sx={{
                              width: '20px',

                              height: '20px',
                            }}
                          />
                        </Button>
                      )}
                      {!props.isLoading ? (
                        <Box
                          component='img'
                          alt=''
                          src={file.type === 'application/pdf' ? fileIcon : file.preview}
                          sx={fileImg}
                        />
                      ) : (
                        props.isLoading && <FileLoaderComponent />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.grey[80],
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: 'inline-block',
                      maxWidth: '95%',
                      mt: '8px',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Box>
              ))
            : initFiles &&
              initFiles.map((file: initFilesProps, i: number) => (
                <Box key={file.preview}>
                  <Box
                    sx={
                      props.filesContainerStyle
                        ? props.filesContainerStyle
                        : filesContainer
                    }
                    key={file.preview}
                  >
                    <Box
                      sx={
                        props.fileContainerStyle
                          ? props.fileContainerStyle
                          : fileContainer
                      }
                    >
                      {!props.isLoading && (
                        <DeleteIcon
                          sx={{
                            position: 'absolute',
                            right: '-0.75rem',
                            top: '-0.75rem',
                            borderRadius: '3px',
                            color: theme.palette.grey[600],
                            background: theme.palette.action.hover,
                            cursor: 'pointer',
                            transition: 'all ease-in-out .3s',
                            ':hover': {
                              color: theme.palette.grey[200],
                              background: theme.palette.grey[500],
                            },
                          }}
                          onClick={(e: MouseEvent) => removeFile(e, i, true)}
                        />
                      )}

                      {!props.isLoading ? (
                        <>
                          <Box
                            component='img'
                            alt=''
                            src={file.preview}
                            sx={props.fileImgStyle ? props.fileImgStyle : fileImg}
                          />
                        </>
                      ) : (
                        props.isLoading && <FileLoaderComponent />
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}

          {errorFileCondition && (
            <Typography variant='body1' gutterBottom color={theme.palette.error.light}>
              {(maxFileCondition || !props.multiple) && (
                <FormattedMessage id='global.max_files' />
              )}
              {(maxFileCondition || !props.multiple) &&
                (props.maxFiles ? props.maxFiles : 1)}
              {fileRejections.map(({ file, errors }) => (
                <Box key={file.name}>
                  {errors.map(e =>
                    e.code === 'too-many-files' ? null : (
                      <span key={e.code}>
                        {file.name}: {e.message}
                      </span>
                    )
                  )}
                </Box>
              ))}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
